import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { GALLERY_VIDEO, getErrorTitle, WEBSITE_TEMPLATE_GALLERY_MAX_FILE_SIZE_IN_BYTES } from "../../../../constants/constants";
import { ReferenceType } from "../../../../models/document";
import { GallerySection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface EditModalProps {
  data: GallerySection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const EditModal: FC<EditModalProps> = ({
  isOpen,
  toggleModal,
  flag,
  setFlag,
  data,
  homeId,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [newFileUrls, setNewFileUrls] = useState<string[]>([]);

  const [images, setImages] = useState<string[]>([]);
  const [signedImageUrls, setSignedImageUrls] = useState<string[]>([]);
  const [loadingImg, setLoadingImg] = useState<boolean>(false);
  const [updatedData, setUpdatedData] = useState<GallerySection | null>(data);

  const defaultValues = {
    description: data?.description || "",
  }

  const { formState: { errors }, handleSubmit, reset, control } = useForm({ defaultValues });

  useEffect(() => {
    setUpdatedData(data);
    setImages(data?.sectionImg || []);
    setSignedImageUrls(data?.sectionSignedImg || []);
  }, [data, isOpen]);

  const handleFileChange = (event) => {
    if (event.files && event.files.length) {
      setSelectedFiles(event.files);
      event.files.forEach((file) => {
        if (file.type.startsWith("video/")) {
          const objectKey = `website/default_template/${homeId}/draft/${GALLERY_VIDEO}-${uuidv4()}`;
          setNewFileUrls((prev) => [...prev, objectKey]);
        } else {
          const objectKey = `website/default_template/${homeId}/draft/GALLERY-${uuidv4()}`;
          setNewFileUrls((prev) => [...prev, objectKey]);
        }
      });
    }
  };

  const uploadGalleryImages = async () => {
    console.log("Uploading gallery images...");
    console.log("New file URLs:", newFileUrls);

    if (!newFileUrls.length) return null;
    try {
      const presignedUrls = await Promise.all(newFileUrls.map((objectKey) => {
        return backendAPIStore.getPresignedUrlForUpload(homeId!, ReferenceType.BUSINESS_HOME, objectKey);
      }));
      await Promise.all(presignedUrls.map((presignedUrl, index) => {
        return backendAPIStore.uploadFileToS3(presignedUrl, selectedFiles[index]);
      }));
      return presignedUrls.map(url => url.split("?")[0]);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      return null;
    }
  };

  const onSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoadingImg(true);
    try {
      await backendAPIStore.init();
      await uploadGalleryImages();
      let allImageUrls = images;
      if (newFileUrls.length) {
        allImageUrls = [...images, ...newFileUrls];
      }

      const apiData: GallerySection = {
        ...updatedData!,
        description: normalizePlainText(data.description, { multiline: true, maxLength: 1200 }),
        sectionImg: allImageUrls,
        modifiedAt: new Date().toISOString(),
      };

      await backendAPIStore.updateTemplateSection(apiData);

    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to upload image', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setFlag(!flag);
      setSelectedFiles([]);
      setNewFileUrls([]);
      setLoadingImg(false);
    }
  };

  const headerElement = renderSurfaceHeader(
    "Manage Gallery",
    "Upload, describe, and curate the photos and videos shown on the website.",
    "pi pi-images",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading: loadingImg,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Gallery",
  });

  const chooseOptions = { icon: 'pi pi-fw pi-images', className: 'custom-choose-btn p-button-rounded p-button-outlined' };
  const cancelOptions = { icon: 'pi pi-fw pi-times', className: 'custom-cancel-btn p-button-rounded p-button-outlined p-button-danger' };

  const fileHeaderTemplate = (options) => {
    const { className, chooseButton, cancelButton } = options;

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {cancelButton}
      </div>
    );
  };

  const handleImageDelete = (index: number) => {
    const updatedSignedUrls = signedImageUrls.filter((_, i) => i !== index);
    setSignedImageUrls(updatedSignedUrls);

    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);

    setUpdatedData((prev) =>
      prev ? { ...prev, sectionSignedImg: updatedSignedUrls, sectionImg: updatedImages } : prev,
    );
  }

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Gallery manager</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Keep your website media current by adding fresh photos or videos and removing outdated items.
          </div>
        </div>
        <div style={appFormSectionStyle}>
          <label htmlFor="description"><strong>Description</strong></label>
          <Controller name="description" control={control} defaultValue={data?.description || ""} render={({ field }) => (
            <InputTextarea id="description" rows={10} cols={20} style={{ width: "100%" }} {...field} className={`p-inputtext p-component ${errors.description ? 'p-invalid' : ''}`} />
          )} />
          {getFormErrorMessage('description')}
        </div>

        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
        <div className="mb-2"><strong>Gallery images/videos</strong></div>
        <FileUpload multiple name="imagePicker" onSelect={handleFileChange} accept="image/*, video/*" maxFileSize={WEBSITE_TEMPLATE_GALLERY_MAX_FILE_SIZE_IN_BYTES}
          headerTemplate={fileHeaderTemplate} chooseOptions={chooseOptions} cancelOptions={cancelOptions}
          mode="advanced"
          emptyTemplate={<p className="m-0">Add new images/videos here by drag and drop or Choose to upload.</p>}
        />
        </div>
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
        <h3 className="mt-0">Uploaded Images/videos: {signedImageUrls.length}</h3>
        <div className="justify-content-center align-items-center d-flex flex-wrap grid">
          {
            signedImageUrls.length > 0 ? signedImageUrls.map((imgUrl, index) => (
              <div key={index} style={{ position: "relative", width: "fit-content" }} className="col">
                {
                  imgUrl && imgUrl.indexOf(GALLERY_VIDEO) !== -1 ? (
                    <video width="150px" height="150px" style={{ objectFit: "cover" }} controls>
                      <source src={imgUrl} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={imgUrl} alt={`Gallery ${index + 1}`} style={{ width: "150px", height: "150px", objectFit: "cover" }} />
                  )
                }
                <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-outlined" style={{ position: "absolute", top: 5, right: 5 }}
                  onClick={(e) => { e.preventDefault(); handleImageDelete(index) }} />
              </div>
            )) : null
          }
        </div>
        </div>
      </form>
    </Dialog>
  );
};

export default EditModal;
